// ASISTENTUL PLANULUI DE SITUAȚIE: discuția prin care omul cere schimbări pe
// plan, în colțul din dreapta-jos al vizualizatorului, ca asistentul de fațadă
// pe machetă. Fără panou (Oana, 31.10.2026): cifrele planului sunt în
// «Indicatori», lângă legendă, iar «Refă planul cu agentul» stă în meniul «⋯»
// al soluției (`nav/BlocSolutie`), lângă «Refă excavația» și «Export PUD».
//
// Agentul (jobul `site_plan_agent`) rulează minute întregi pe server: desenează
// circulația, locurile de parcare, trotuarele și zonele, și RESCRIE documentul
// planului la fiecare lot acceptat — omul vede planșa crescând. Nimic nu se
// scrie direct de aici: pe plan totul trece prin agent, care verifică fiecare
// lot pe reguli (`asistent/unelteSituatie.ts`, `preda_agentului_plan`).
//
// RULAREA E RĂSPUNSUL LA CERERE (Oana, 30.10.2026): cardul ei — starea, firul,
// cifrele de la sfârșit — stă ÎN DISCUȚIE, sub mesajul omului care a pornit-o
// (sau sub ultimul mesaj, la o rulare pornită din meniu). Cât rulează, cardul
// crește pe loc; când se încheie, rămâne în istoric înghețat, iar o rulare nouă
// primește cardul ei. Istoricul e al ecranului (`rulari.ts`, `sessionStorage`
// pe plan); serverul știe doar ultima rulare. Cât agentul lucrează, bula
// discuției pulsează (`Asistent`), ca omul să vadă și cu discuția închisă.
//
// Firul vine pe același drum ca la fațadă (`fatada/firAgent.ts`): pentru o
// soluție de plan, endpoint-ul întoarce ultimul job `site_plan_agent`. Hook-ul
// sondează doar cât registrul vede jobul sau firul s-a declarat deschis, iar
// registrul îl vede cu întârziere; de aceea, cât o rulare pornită de aici
// așteaptă primele ei rânduri, sondajul e FORȚAT — iar `rulari.ts` nu ia acel
// `ruleaza` forțat drept dovadă că jobul nou a fost văzut.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../api";
import Asistent, { FirulAgentului, type TexteAgent } from "../../asistent/Asistent";
import { PASII_SITUATIEI, legatDeSolutie, unelteSituatie, type ContextSituatie } from "../../asistent/unelteSituatie";
import { useRolProiect } from "../../components/rol";
import { numar, useT, useTn } from "../../i18n";
import { useRegistruOptional } from "../../nav/Registru";
import { numeRand } from "../../nav/selectie";
import { useFirAgent } from "../fatada/firAgent";
import type { StareFir } from "../fatada/firAgent";
import { agentulPlanului, ruleazaInRegistru, scrierileFirului, ultimelePropozitii,
         type AgentPlanRezumat } from "./firPlan";
import { asteaptaPrimiiPasi, citesteIstoricul, cuRezumatul, cuRularePornita, faraPornirileUitate, laFir,
         rulareaVie, scrieIstoricul, type Rulare } from "./rulari";
import "./asistentPlan.css";

type Props = {
  projectId: string;
  /** Soluția de plan de situație deschisă. */
  planId: string;
  /** Rezumatul rândului cu care s-a deschis ecranul; registrul viu îl bate
   *  când îl are (el se rescrie la fiecare lot). */
  summary?: unknown;
  /** AGENTUL A SCRIS PE PLAN (un lot acceptat, sfârșitul rulării): planșa își
   *  recitește documentul, cu camera unde era. Documentul se recitește oricum
   *  după `updated_at` din registru; asta e recitirea MOALE, pe loc. */
  laSchimbare?: () => void;
  /** Locul discuției și al bulei ei în coloana vizualizatorului (v. `Asistent::loc`). */
  loc: { panou: HTMLElement | null; buton: HTMLElement | null };
};

/** Cât ține «a pornit» fără ca firul sau registrul să vadă jobul (registrul
 *  e sondat la 3 s; firul, forțat, la 2,5 s). */
const ASTEPT_REGISTRUL_MS = 90_000;

export default function AsistentPlan({ projectId, planId, summary, laSchimbare, loc }: Props) {
  const t = useT();
  const { potScrie } = useRolProiect();
  const registrul = useRegistruOptional();
  const rand = registrul?.registru?.solutii.find((s) => s.id === planId);
  const rezumat = useMemo<AgentPlanRezumat | null>(
    () => agentulPlanului(rand?.summary ?? summary), [rand?.summary, summary]);

  // ISTORICUL RULĂRILOR, al planului deschis: pe alt plan, al lui (citit din
  // `sessionStorage` chiar la desenare, ca să nu treacă un cadru cu istoricul
  // planului vechi sub discuția celui nou)
  const [istoric, setIstoric] = useState(() => ({ planId, rulari: citesteIstoricul(planId) }));
  if (istoric.planId !== planId) setIstoric({ planId, rulari: citesteIstoricul(planId) });
  const rulari = istoric.planId === planId ? istoric.rulari : [];
  const schimbaRularile = useCallback((f: (r: Rulare[]) => Rulare[]) =>
    setIstoric((s) => { const rulari = f(s.rulari); return rulari === s.rulari ? s : { ...s, rulari }; }), []);
  useEffect(() => { if (istoric.planId === planId) scrieIstoricul(planId, istoric.rulari); },
            [istoric, planId]);
  const vie = rulareaVie(rulari);
  const pornita = vie?.stare === "pornita";

  // FIRUL: sondat cât registrul vede jobul — și, FORȚAT, cât o rulare pornită
  // de aici își așteaptă primele rânduri (altfel ar porni abia când registrul,
  // sondat la 3 s, observă jobul). Cât e forțat, `fir.ruleaza` nu spune că
  // jobul nou a fost văzut; `laFir` se uită la rânduri, nu la el.
  const ruleazaReg = ruleazaInRegistru(registrul?.registru, planId);
  const fir = useFirAgent(projectId, planId, ruleazaReg || pornita);

  // firul viu leagă rularea pornită de aici (sau una găsită) și o îngheață la sfârșit
  const rezumatRef = useRef(rezumat);
  rezumatRef.current = rezumat;
  /** Firul de pe ecran în clipa pornirii: al jobului vechi (v. `firDinainte`). */
  const firRef = useRef(fir.randuri);
  firRef.current = fir.randuri;
  // `la` al ultimului rând: firul jobului nou poate avea tot atâtea rânduri ca
  // al celui vechi, dar nu aceeași oră
  const ultimLa = fir.randuri.length ? fir.randuri[fir.randuri.length - 1].la ?? null : null;
  useEffect(() => { schimbaRularile((r) => laFir(r, fir, rezumatRef.current)); },
            [fir.ruleaza, fir.randuri.length, ultimLa, schimbaRularile]);
  // cifrele de la sfârșit, pe ultima rulare încheiată
  useEffect(() => { schimbaRularile((r) => cuRezumatul(r, rezumat)); }, [rezumat, schimbaRularile]);
  // O PORNIRE pe care nici registrul n-o vede rulând: după limită, cardul
  // pleacă și discuția se deblochează. Văzută de registru (un `pending` lung
  // la coadă), așteaptă oricât primele rânduri.
  useEffect(() => {
    if (!pornita || ruleazaReg) return;
    const ceas = setTimeout(() => schimbaRularile((r) => faraPornirileUitate(r, ASTEPT_REGISTRUL_MS)),
                            ASTEPT_REGISTRUL_MS + 50);
    return () => clearTimeout(ceas);
  }, [pornita, ruleazaReg, schimbaRularile]);
  const agentLucreaza = fir.ruleaza || pornita;

  // CE A SCRIS AGENTUL apare pe planșă pe loc: după fiecare lot scris și la
  // sfârșitul rulării. Numai rândurile NOI ale unei rulări văzute rulând — firul
  // unei rulări vechi, citit la deschidere, n-are ce reciti.
  const laSchimbareRef = useRef(laSchimbare);
  laSchimbareRef.current = laSchimbare;
  const vazutRuland = useRef(false);
  if (fir.ruleaza) vazutRuland.current = true;
  const scrieri = scrierileFirului(fir.randuri);
  const scrieriVazute = useRef<number | null>(null);
  useEffect(() => {
    if (!fir.ruleaza && !vazutRuland.current) { scrieriVazute.current = scrieri; return; }
    if (scrieriVazute.current !== null && scrieri > scrieriVazute.current) laSchimbareRef.current?.();
    scrieriVazute.current = scrieri;
  }, [scrieri, fir.ruleaza]);
  const rulaInainte = useRef(false);
  useEffect(() => {
    if (rulaInainte.current && !fir.ruleaza) {
      void (async () => {
        if (registrul) await registrul.reciteste();
        laSchimbareRef.current?.();
      })();
    }
    rulaInainte.current = fir.ruleaza;
  }, [fir.ruleaza, registrul]);

  /** Al câtelea e ultimul mesaj al omului din discuție (−1 = niciunul): sub el
   *  intră cardul rulării pe care o pornește unealta turului. */
  const ultimulOm = useRef(-1);
  const laMesaje = useCallback((n: number) => { ultimulOm.current = n - 1; }, []);
  const laDiscutieNoua = useCallback(() => schimbaRularile(() => []), [schimbaRularile]);

  /** Pornește jobul cu `cerere` = editarea planului de acum (unealta
   *  `preda_agentului_plan` a asistentului). 409 = rulează deja. Reușita pune
   *  cardul rulării în discuție, sub ultimul mesaj al omului. */
  const porneste = useCallback(async (cerere: string, poza?: string | null) => {
    try {
      await api.startJob(projectId, "site_plan_agent", {
        upstream_solution_id: planId, cerere, ...(poza ? { poza } : {}),
      });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      return { ok: false, motiv: e.status === 409 ? t("agent_plan.deja_ruleaza") : (e.message ?? String(err)) };
    }
    const dupaOm = ultimulOm.current >= 0 ? ultimulOm.current : null;
    schimbaRularile((r) => cuRularePornita(r, "cerere", dupaOm, rezumatRef.current, firRef.current));
    void registrul?.reciteste();
    return { ok: true };
  }, [projectId, planId, registrul, t, schimbaRularile]);

  /** Ultima poză atașată în discuție, pentru agent (`preda_agentului_plan`). */
  const ultimaPoza = useRef<string | null>(null);

  // CONTEXTUL ASISTENTULUI, ținut stabil și LEGAT de planul lui: uneltele se
  // leagă o dată, citesc prin referință «ce e pe ecran acum», și refuză după
  // ce ecranul a trecut pe alt plan
  const ref = useRef({ rezumat, agentLucreaza, randuri: fir.randuri, rand, planId, porneste });
  ref.current = { rezumat, agentLucreaza, randuri: fir.randuri, rand, planId, porneste };
  const ctxAsistent = useMemo<ContextSituatie>(() => legatDeSolutie({
    stare: () => ({
      plan: { id: planId, nume: ref.current.rand ? numeRand(ref.current.rand) : null },
      agent: ref.current.rezumat,
      agent_lucreaza: ref.current.agentLucreaza,
      ultimele_propozitii: ultimelePropozitii(ref.current.randuri),
    }),
    predaAgentului: potScrie
      ? (cerere, cuPoza) => ref.current.porneste(cerere, cuPoza ? ultimaPoza.current : null)
      : null,
  }, planId, () => ref.current.planId), [planId, potScrie]);
  const unelte = useMemo(() => unelteSituatie(ctxAsistent), [ctxAsistent]);

  const texteAgent = useMemo<TexteAgent>(() => ({
    titlu: t("agent_plan.titlu"), lucreaza: t("agent_plan.lucreaza"),
    blocata: t("agent_plan.discutie_blocata"), buton: t("agent_plan.buton_lucreaza"),
  }), [t]);

  // CARDURILE DIN DISCUȚIE: cel viu citește firul, cele vechi rândurile lor
  const carduri = useMemo(() => rulari.map((r) => ({
    cheie: r.id, dupaOm: r.dupaOm,
    nod: <CardRulare rulare={r} fir={r.stare === "ruleaza" ? fir : null} agent={texteAgent} />,
  })), [rulari, fir, texteAgent]);

  // Cât lucrează agentul, câmpul e oprit: serverul refuză oricum un al doilea job.
  return (
    <Asistent projectId={projectId} cod={planId} potScrie={potScrie} unde="situatie"
              unelte={unelte} titlu={t("agent_plan.asistent")}
              subtitlu={rand ? numeRand(rand) : undefined} loc={loc} fir={fir} agent={texteAgent}
              prietenos pasi={PASII_SITUATIEI}
              blocat={agentLucreaza ? t("agent_plan.discutie_blocata") : undefined}
              laPoza={(p) => { ultimaPoza.current = p; }}
              carduri={carduri} laMesaje={laMesaje} laDiscutieNoua={laDiscutieNoua} />
  );
}

/** Cifrele unei rulări (pe cardul ei din discuție): locurile față de țintă,
 *  verdele față de cotă, loturile, blocajele. */
export function Cifre({ rezumat }: { rezumat: AgentPlanRezumat }) {
  const t = useT();
  const tn = useTn();
  const pct = (v: number | null) => numar(v, { maximumFractionDigits: 1 });
  const ora = rezumat.la
    ? new Date(rezumat.la).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <>
      <div className="pa-cifre">
        <div className="pa-cifra">
          <span>{t("agent_plan.locuri")}</span>
          <b>{rezumat.locuri_tinta != null
              ? t("agent_plan.din_tinta", { locuri: numar(rezumat.locuri), tinta: numar(rezumat.locuri_tinta) })
              : numar(rezumat.locuri)}</b>
        </div>
        <div className="pa-cifra">
          <span>{t("agent_plan.verde")}</span>
          <b>{rezumat.verde_cota_pct != null
              ? t("agent_plan.verde_cota", { pct: pct(rezumat.verde_pct), cota: pct(rezumat.verde_cota_pct) })
              : `${pct(rezumat.verde_pct)} %`}</b>
        </div>
      </div>
      <div className="pa-meta">
        {[
          t("agent_plan.loturi", { ok: rezumat.loturi.ok, respinse: rezumat.loturi.respinse }),
          rezumat.tururi != null && tn("agent_plan.tururi#n", rezumat.tururi),
          rezumat.cost_usd != null && t("agent_plan.cost", { cost: numar(rezumat.cost_usd, { maximumFractionDigits: 2 }) }),
          rezumat.revizie != null && t("agent_plan.revizie", { n: rezumat.revizie }),
          ora,
        ].filter(Boolean).join(" · ")}
      </div>
      {rezumat.blocaje.length > 0 && (
        <>
          <span className="pa-meta">{tn("agent_plan.blocaje#n", rezumat.blocaje.length)}</span>
          <ul className="pa-blocaje">
            {rezumat.blocaje.map((b, i) => (
              <li key={i}><b>{b.obiect}</b><span>{b.motiv}</span></li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/** CARDUL UNEI RULĂRI, în discuție, sub cererea ei. Vie: firul care crește
 *  (`fir`). Abia pornită, sau văzută rulând fără niciun rând încă: un rând cu
 *  punctul care pulsează. Încheiată: firul ei înghețat (rezultatul sus, pașii
 *  strânși) și cifrele rulării, când au venit. Sursa se spune numai când nu e
 *  un mesaj al omului deasupra (rularea pornită din meniul soluției, o rulare
 *  găsită la deschidere). */
export function CardRulare({ rulare, fir, agent }: { rulare: Rulare; fir: StareFir | null; agent: TexteAgent }) {
  const t = useT();
  const sursa = rulare.sursa === "buton" ? t("agent_plan.card.din_buton")
    : rulare.sursa === "gasita" ? t("agent_plan.card.gasita") : null;
  if (asteaptaPrimiiPasi(rulare, fir)) {
    return (
      <section className="ob-as-fir lucreaza pa-card" aria-live="polite">
        <span className="ob-as-fir-linie" aria-hidden="true"><span /></span>
        <div className="ob-as-fir-cap">
          <b>{agent.titlu}</b><span>{agent.lucreaza}</span>
          <div className="ob-as-sp" />
          {sursa && <span>{sursa}</span>}
        </div>
        <div className="pa-card-pornita"><span className="ob-as-punct" />{t("agent_plan.card.pornita")}</div>
      </section>
    );
  }
  const firul: StareFir = fir ?? { ruleaza: false, randuri: rulare.randuri };
  return (
    <div className="pa-card">
      {sursa && <span className="pa-meta pa-card-sursa">{sursa}</span>}
      <FirulAgentului fir={firul} agent={agent} />
      {rulare.stare === "incheiata" && rulare.rezumat && (
        <div className="pa-card-cifre"><Cifre rezumat={rulare.rezumat} /></div>
      )}
    </div>
  );
}
